import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateDiscountCouponRequest,
  CreateDiscountCouponRequestSchema,
  type DiscountCouponDetail,
  type DiscountCouponListItem,
  type UpdateDiscountCouponRequest,
  UpdateDiscountCouponRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  useCreateDiscountCoupon,
  useCurrencyOptions,
  useUpdateDiscountCoupon,
} from '../use-discount-coupons';

interface DiscountCouponFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  coupon?: DiscountCouponDetail | DiscountCouponListItem | null;
}

const KIND_OPTIONS = [
  { value: 'PERCENT', label: 'Percent off' },
  { value: 'FLAT', label: 'Flat amount' },
] as const;

const CREATE_DEFAULTS: CreateDiscountCouponRequest = {
  code: '',
  name: '',
  kind: 'PERCENT',
  value: 0,
  maxRedemptions: 0,
  maxPerCustomer: 1,
  isActive: true,
  sortOrder: 0,
};

/**
 * Helper: convert a date input YYYY-MM-DD string to an ISO timestamp string.
 * Returns null when the input is empty, preserving the nullable contract.
 */
const toIso = (v: string | null | undefined): string | null => {
  if (!v || !v.trim()) return null;
  return new Date(v).toISOString();
};

/**
 * Helper: slice an ISO timestamp down to the YYYY-MM-DD portion expected by
 * <input type="date">. Returns '' when the value is null / undefined.
 */
const toDateInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 */
const CouponFields = ({
  pending,
  watchedKind,
  currencyOptions,
}: {
  pending: boolean;
  watchedKind: string;
  currencyOptions: ReadonlyArray<{ value: string; label: string }>;
}): React.JSX.Element => (
  <>
    <UzFormField name="code">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value.toLowerCase())}
          label="Code"
          placeholder="e.g. summer10"
          hint="Lowercase letters, numbers, and underscores."
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Summer 10% Off"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="kind">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Kind"
          required
          options={KIND_OPTIONS as ReadonlyArray<{ value: string; label: string }>}
          value={(field.value as string) || undefined}
          onValueChange={field.onChange}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="value">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Value"
          precision={2}
          hint={watchedKind === 'PERCENT' ? '%' : undefined}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="currencyId">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Currency"
          placeholder="None (relevant for FLAT coupons)"
          options={currencyOptions}
          value={(field.value as string) || undefined}
          onValueChange={(v) => field.onChange(v || null)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="minOrderValue">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Minimum order value"
          precision={2}
          hint="Optional. Coupon only applies when order total meets this threshold."
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? null)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="maxRedemptions">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Max redemptions"
          precision={0}
          hint="0 = unlimited"
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="maxPerCustomer">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Max per customer"
          precision={0}
          hint="0 = unlimited"
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 1)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="validFrom">
      {({ field, error }) => (
        <UzInput
          name={field.name}
          type="date"
          // Store ISO or null in the form; display as YYYY-MM-DD for the input.
          value={toDateInput(field.value as string | null | undefined)}
          onChange={(e) => field.onChange(toIso(e.target.value))}
          label="Valid from"
          hint="Optional start of the validity window."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="validUntil">
      {({ field, error }) => (
        <UzInput
          name={field.name}
          type="date"
          // Store ISO or null in the form; display as YYYY-MM-DD for the input.
          value={toDateInput(field.value as string | null | undefined)}
          onChange={(e) => field.onChange(toIso(e.target.value))}
          label="Valid until"
          hint="Optional end of the validity window (must be on or after valid-from)."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive coupons are not accepted at checkout."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const DiscountCouponFormDrawer = ({
  open,
  onOpenChange,
  mode,
  coupon,
}: DiscountCouponFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createCoupon = useCreateDiscountCoupon();
  const updateCoupon = useUpdateDiscountCoupon();
  const pending = createCoupon.isPending || updateCoupon.isPending;

  const currencyOptionsQuery = useCurrencyOptions();
  const currencyOptions = (currencyOptionsQuery.data ?? []).map((c) => ({
    value: c.id,
    label: c.symbol ? `${c.code} (${c.symbol})` : c.code,
  }));

  const createForm = useForm<CreateDiscountCouponRequest>({
    resolver: zodResolver(CreateDiscountCouponRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });

  // Initialise the edit form directly from the coupon prop so the form holds
  // valid Zod-parseable data from the first render, not just after the effect.
  // Date fields stay as ISO strings (or null) in the form state; the inputs
  // convert them to YYYY-MM-DD for display only.
  const editDefaults: UpdateDiscountCouponRequest | Record<string, never> = coupon
    ? {
        code: coupon.code,
        name: coupon.name,
        kind: coupon.kind,
        value: coupon.value,
        currencyId: coupon.currencyId ?? undefined,
        minOrderValue: coupon.minOrderValue ?? undefined,
        maxRedemptions: coupon.maxRedemptions,
        maxPerCustomer: coupon.maxPerCustomer,
        validFrom: coupon.validFrom ?? null,
        validUntil: coupon.validUntil ?? null,
        sortOrder: coupon.sortOrder,
        isActive: coupon.isActive,
      }
    : {};
  const updateForm = useForm<UpdateDiscountCouponRequest>({
    resolver: zodResolver(UpdateDiscountCouponRequestSchema),
    defaultValues: editDefaults,
  });

  // The active form's kind field - used to switch the value hint.
  const watchedKind = isCreate
    ? (createForm.watch('kind') ?? 'PERCENT')
    : (updateForm.watch('kind') ?? 'PERCENT');

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (coupon) {
      updateForm.reset({
        code: coupon.code,
        name: coupon.name,
        kind: coupon.kind,
        value: coupon.value,
        currencyId: coupon.currencyId ?? undefined,
        minOrderValue: coupon.minOrderValue ?? undefined,
        maxRedemptions: coupon.maxRedemptions,
        maxPerCustomer: coupon.maxPerCustomer,
        // Keep as ISO (or null) in form state; the input converts for display.
        validFrom: coupon.validFrom ?? null,
        validUntil: coupon.validUntil ?? null,
        sortOrder: coupon.sortOrder,
        isActive: coupon.isActive,
      });
    }
  }, [open, isCreate, coupon, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // Date fields are stored as ISO strings (or null) in the form - the Zod
    // schema validates them directly; no further conversion is needed here.
    await createCoupon.mutateAsync(values);
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!coupon) return;
    await updateCoupon.mutateAsync({ id: coupon.id, payload: values });
    onOpenChange(false);
  });

  const fields = (
    <CouponFields
      pending={pending}
      watchedKind={watchedKind as string}
      currencyOptions={currencyOptions}
    />
  );

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add coupon' : `Edit ${coupon?.name ?? 'coupon'}`}
      description="The code is the stable identifier customers enter at checkout."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
